import * as store from "../state/store.js";
import type { Layer } from "../state/types.js";
import { nextColor, releaseColor } from "../utils/colors.js";
import { checkpoint } from "./history.js";
import { toast } from "./ui.js";
import { rebuildGlbFromCurrent } from "./glb.js";

// Add a new empty layer. Returns the created layer's name, or null on failure
// (empty name or already-exists).
export function addLayer(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;

  const layers = store.layers();
  if (layers[trimmed]) {
    toast(`Layer "${trimmed}" already exists`);
    return null;
  }

  const { color, used } = nextColor(store.peek.usedColors());
  const newLayer: Layer = {
    name: trimmed,
    color,
    visible: true,
    locked: false,
    entityIds: [],
  };

  store.batch(() => {
    store.write.layers({ ...layers, [trimmed]: newLayer });
    store.write.layerOrder([...store.layerOrder(), trimmed]);
    store.write.usedColors(used);
    store.write.selectedLayer(trimmed);
    store.write.selectedLayerNames(new Set([trimmed]));
  });
  checkpoint("Add Layer");
  toast(`Added layer: ${trimmed}`);
  return trimmed;
}

// Ask the layer panel to enter inline rename mode for `name` on its next
// render. The panel watches `store.pendingRenameLayer()` and clears it back
// to null once it's flipped its editing state. Refuses on "0" and locked
// layers with a toast, so the user gets feedback whether the request came
// from the context menu, a keyboard shortcut, or anywhere else.
export function requestLayerRename(name: string): void {
  if (name === "0") {
    toast('Cannot rename the default layer "0"');
    return;
  }
  const layer = store.layers()[name];
  if (!layer) return;
  if (layer.locked) {
    toast(`Cannot rename locked layer "${name}"`);
    return;
  }
  store.write.pendingRenameLayer(name);
}

export function toggleVisibility(name: string): void {
  const layers = store.layers();
  const layer = layers[name];
  if (!layer) return;
  store.write.layers({
    ...layers,
    [name]: { ...layer, visible: !layer.visible },
  });
  // 3D preview mirrors the 2D visibility — hidden layers drop out of the
  // extrusion and their marks disappear from the top face. `rebuildGlbFromCurrent`
  // now filters by visibility, so a single call does the job.
  rebuildGlbFromCurrent();
}

// Locking a layer keeps its entities visible on the canvas but blocks any
// selection interaction (click, drag-select, select-all) from including them.
// When a layer is locked we also drop any currently-selected entities that
// live on it, so the "selected" state stays consistent with what the user is
// now allowed to act on.
export function toggleLock(name: string): void {
  const layers = store.layers();
  const layer = layers[name];
  if (!layer) return;
  const nextLocked = !layer.locked;
  const nextLayers = { ...layers, [name]: { ...layer, locked: nextLocked } };
  store.batch(() => {
    store.write.layers(nextLayers);
    if (nextLocked) {
      const sel = store.selectedEntityIds();
      if (sel.size > 0) {
        const lockedIds = new Set(layer.entityIds);
        let changed = false;
        const next = new Set<number>();
        for (const id of sel) {
          if (lockedIds.has(id)) changed = true;
          else next.add(id);
        }
        if (changed) store.write.selectedEntityIds(next);
      }
    }
  });
}

export function renameLayer(oldName: string, newName: string): boolean {
  if (oldName === "0") {
    toast('Cannot rename the default layer "0"');
    return false;
  }
  const trimmed = newName.trim();
  if (!trimmed || trimmed === oldName) return false;

  const layers = store.layers();
  const layer = layers[oldName];
  if (!layer) return false;
  if (layer.locked) {
    toast(`Cannot rename locked layer "${oldName}"`);
    return false;
  }
  if (layers[trimmed]) {
    toast(`Layer "${trimmed}" already exists`);
    return false;
  }

  const nextLayers = { ...layers, [trimmed]: { ...layer, name: trimmed } };
  delete nextLayers[oldName];

  const nextOrder = store.layerOrder().map((n) => (n === oldName ? trimmed : n));
  const nextEntities = store.entities().map((e) =>
    e.layer === oldName ? { ...e, layer: trimmed } : e
  );

  store.batch(() => {
    store.write.layers(nextLayers);
    store.write.layerOrder(nextOrder);
    store.write.entities(nextEntities);
    if (store.selectedLayer() === oldName) store.write.selectedLayer(trimmed);
    const selNames = store.selectedLayerNames();
    if (selNames.has(oldName)) {
      const next = new Set(selNames);
      next.delete(oldName);
      next.add(trimmed);
      store.write.selectedLayerNames(next);
    }
  });
  checkpoint("Rename Layer");
  toast(`Renamed to: ${trimmed}`);
  // Rename doesn't change WHICH entities belong to WHICH layer, but it does
  // change the layer name embedded in the GLB's extras.profile[].layer
  // metadata. Rebuild so the preview matches the current model.
  rebuildGlbFromCurrent();
  return true;
}

export function deleteLayer(name: string): boolean {
  if (name === "0") {
    toast('Cannot delete the default layer "0"');
    return false;
  }
  const layers = store.layers();
  const layer = layers[name];
  if (!layer) return false;
  if (layer.locked) {
    toast(`Cannot delete locked layer "${name}"`);
    return false;
  }

  const zeroExisted = !!layers["0"];
  const nextLayers = ensureZero(layers);
  const zero = nextLayers["0"]!;
  const movedCount = layer.entityIds.length;
  nextLayers["0"] = { ...zero, entityIds: [...zero.entityIds, ...layer.entityIds] };
  delete nextLayers[name];

  // "0" is the DXF default and always sits at the top of the layer list.
  const nextOrder = ["0", ...store.layerOrder().filter((n) => n !== name && n !== "0")];

  const nextEntities = store.entities().map((e) =>
    e.layer === name ? { ...e, layer: "0" } : e
  );

  // If we just materialised "0" as a side effect of this delete AND the
  // panel has a filter that would hide it, clear the filter so the user
  // isn't surprised by a "vanished" layer.
  const shouldClearFilter =
    !zeroExisted && !("0".toLowerCase().includes(store.filterText().toLowerCase()));

  store.batch(() => {
    store.write.layers(nextLayers);
    store.write.layerOrder(nextOrder);
    store.write.entities(nextEntities);
    store.write.usedColors(releaseColor(store.peek.usedColors(), layer.color));
    const selNames = new Set(store.selectedLayerNames());
    selNames.delete(name);
    store.write.selectedLayerNames(selNames);
    if (store.selectedLayer() === name) store.write.selectedLayer("0");
    if (shouldClearFilter) store.write.filterText("");
  });
  checkpoint("Delete Layer");
  toast(
    movedCount > 0
      ? `Deleted "${name}" — moved ${movedCount} ${
          movedCount === 1 ? "entity" : "entities"
        } to layer "0"`
      : `Deleted layer: ${name}`
  );
  // Moving entities to "0" can change what the extrusion looks like:
  // `dxf_2_gltf.rings_to_faces` runs even-odd XOR fill PER LAYER, so
  // merging two layers can cancel/reveal nested holes that used to sit on
  // different layers. Rebuild the GLB from the current model.
  if (movedCount > 0) rebuildGlbFromCurrent();
  return true;
}

export function deleteSelectedLayers(): number {
  const layers = store.layers();
  const names = Array.from(store.selectedLayerNames()).filter(
    (n) => n !== "0" && !layers[n]?.locked
  );
  if (names.length === 0) {
    toast("No deletable layers selected");
    return 0;
  }
  let count = 0;
  for (const name of names) {
    if (deleteLayer(name)) count += 1;
  }
  return count;
}

// Merge every currently-selected non-"0" layer into layer "0".
export function mergeSelectedLayers(): number {
  const names = Array.from(store.selectedLayerNames()).filter((n) => n !== "0");
  if (names.length === 0) {
    toast('Cannot merge the default layer "0"');
    return 0;
  }
  const layers = ensureZero(store.layers());
  const zero = layers["0"]!;
  const gatheredIds: number[] = [];
  const nextEntities = store.entities().map((e) => {
    if (names.includes(e.layer)) {
      gatheredIds.push(e.id);
      return { ...e, layer: "0" };
    }
    return e;
  });
  layers["0"] = { ...zero, entityIds: [...zero.entityIds, ...gatheredIds] };
  let used = store.peek.usedColors();
  for (const name of names) {
    if (layers[name]) {
      used = releaseColor(used, layers[name]!.color);
      delete layers[name];
    }
  }
  // "0" always pinned to the top of layerOrder.
  const nextOrder = ["0", ...store.layerOrder().filter((n) => !names.includes(n) && n !== "0")];
  // Clear the filter if it would hide the merge target ("0") the user just
  // consolidated everything into.
  const shouldClearFilter = !("0".toLowerCase().includes(store.filterText().toLowerCase()));

  store.batch(() => {
    store.write.layers(layers);
    store.write.layerOrder(nextOrder);
    store.write.entities(nextEntities);
    store.write.usedColors(used);
    store.write.selectedLayerNames(new Set(["0"]));
    store.write.selectedLayer("0");
    store.write.selectedEntityIds(new Set());
    if (shouldClearFilter) store.write.filterText("");
  });
  checkpoint("Merge Layers");
  toast(`Merged ${names.length} layers into "0"`);
  // Merging changes per-layer XOR partitioning — same reason deleteLayer
  // rebuilds. Coalesces with any in-flight build via startGlbBuild's
  // AbortController.
  rebuildGlbFromCurrent();
  return names.length;
}

export function changeLayerColor(name: string, color: string): void {
  const layers = store.layers();
  const layer = layers[name];
  if (!layer) return;
  let used = releaseColor(store.peek.usedColors(), layer.color);
  used = [...used, color];
  store.batch(() => {
    store.write.layers({ ...layers, [name]: { ...layer, color } });
    store.write.usedColors(used);
  });
  checkpoint("Change Layer Color");
}

export function selectLayer(
  name: string,
  opts: { additive?: boolean; range?: boolean; preserveEntities?: boolean } = {}
): void {
  const current = store.selectedLayerNames();
  const anchor = store.selectedLayer();
  // `preserveEntities` keeps the current entity selection intact — used when
  // right-clicking a layer row just to target the context menu at that layer.
  // Clearing entities on right-click surprised the user (it wiped the very
  // selection they wanted to act on from the layer menu).
  const clearEntities = !opts.preserveEntities;

  if (opts.range && anchor) {
    const order = store.layerOrder();
    const a = order.indexOf(anchor);
    const b = order.indexOf(name);
    if (a >= 0 && b >= 0) {
      const [start, end] = a < b ? [a, b] : [b, a];
      const next = new Set<string>();
      for (let i = start; i <= end; i++) next.add(order[i]!);
      store.batch(() => {
        store.write.selectedLayerNames(next);
        store.write.selectedLayer(name);
        if (clearEntities) store.write.selectedEntityIds(new Set());
      });
      return;
    }
  }

  if (opts.additive) {
    const next = new Set(current);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    const nextAnchor = next.has(name) ? name : anchor && next.has(anchor) ? anchor : Array.from(next)[0] ?? null;
    store.batch(() => {
      store.write.selectedLayerNames(next);
      store.write.selectedLayer(nextAnchor);
    });
    return;
  }

  store.batch(() => {
    store.write.selectedLayerNames(new Set([name]));
    store.write.selectedLayer(name);
    if (clearEntities) store.write.selectedEntityIds(new Set());
  });
}

function ensureZero(layers: Record<string, Layer>): Record<string, Layer> {
  if (layers["0"]) return { ...layers };
  const { color, used } = nextColor(store.peek.usedColors());
  store.write.usedColors(used);
  return {
    ...layers,
    "0": { name: "0", color, visible: true, locked: false, entityIds: [] },
  };
}
